import React from 'react';
import { styles } from './AddedToCartAlert.css';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import resizeShopifyImage from '@jbhi-fi/utils/src/resizeShopifyImage/resizeShopifyImage';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useIdPrefix } from '../IdPrefix';
import PromoTag from '@jbhi-fi/lanyard-ui/components/PromoTag';
import { ProductImageFallback } from '../ProductImageFallback/ProductImageFallback';
import CartIcon from '@jbhi-fi/lanyard-ui/components/Icons/Cart';

export type AddedToCartAlertProps = {
  onReviewCartClick: React.MouseEventHandler<HTMLButtonElement>;
  onContinueShoppingClick: React.MouseEventHandler<HTMLButtonElement>;
  productImageSrc?: string;
  productTitle?: string;
  bonusProducts?: any;
  variant?: 'default' | 'minimalist';
  background?: 'success' | 'grey' | 'none';
  isAddedToCart?: boolean;
  shouldPlayAddedAnimation?: boolean;
} & (
  | { variant?: 'default'; onCheckoutClick: React.MouseEventHandler<HTMLButtonElement> }
  | { variant?: 'minimalist'; onCheckoutClick?: () => void }
);
export const AddedToCartAlert = ({
  productImageSrc,
  productTitle,
  onCheckoutClick,
  onReviewCartClick,
  onContinueShoppingClick,
  bonusProducts = [],
  variant = 'default',
  background = 'success',
  isAddedToCart = false,
  shouldPlayAddedAnimation = false,
}: AddedToCartAlertProps) => {
  const testIdPrefix = useIdPrefix() || '';

  const bonusProductsSection = (
    <div>
      {bonusProducts.map((bonusProduct: any, index: number) => (
        <div key={index} className={styles.bonusProductContent}>
          {bonusProducts.length > 1 && index === 0 && (
            <div>
              <PromoTag size="sm" label="Free Bonus Items" />
            </div>
          )}
          <div className={styles.bonusProductItem({ isSingleItem: bonusProducts.length == 1 })}>
            {bonusProduct.product_image ? (
              <ProgressiveImage
                className={styles.bonusProductImage}
                placeholderSrc={resizeShopifyImage(bonusProduct.product_image, '32x32')}
                src={resizeShopifyImage(bonusProduct.product_image, '240x240')}
                alt={`${bonusProduct.title} image`}
                width={30}
                height={30}
              />
            ) : (
              <ProductImageFallback className={styles.bonusProductImage} />
            )}
            <div className={styles.bonusProductTitle}>
              {bonusProducts.length === 1 && <PromoTag size="sm" label="Free Bonus Item" />}
              <span
                className={styles.productTitle}
                data-testid={`${testIdPrefix ? testIdPrefix : ''}bonus-product-title`}
              >
                {bonusProduct.title}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (variant === 'minimalist')
    return (
      <div className={styles.addedToCartAlert({ background })} data-testid={`${testIdPrefix}added-to-cart-alert`}>
        {(shouldPlayAddedAnimation && isAddedToCart) || !shouldPlayAddedAnimation ? (
          <div className={styles.heading}>
            <Icon name="Tick" /> Item added to your cart!
          </div>
        ) : null}
        <div className={styles.contents({ variant })}>
          <Button onClick={onContinueShoppingClick} type="link" label="Continue shopping" />
          <Button
            onClick={onReviewCartClick}
            type="linkWithIcon"
            iconPosition="left"
            icon={<CartIcon />}
            label="Review cart"
          />
        </div>
      </div>
    );

  return (
    <div className={styles.addedToCartAlert({ background })} data-testid={`${testIdPrefix}added-to-cart-alert`}>
      <h5 className={styles.heading} data-testid={`${testIdPrefix}added-to-cart-alert-heading`}>
        <Icon name="Tick" /> Item added to your cart!
      </h5>
      <div className={styles.contents({ variant })}>
        <div>
          <div className={styles.product}>
            {productImageSrc ? (
              <ProgressiveImage
                className={styles.productImage}
                placeholderSrc={resizeShopifyImage(productImageSrc, '32x32')}
                src={resizeShopifyImage(productImageSrc, '240x240')}
                alt={`${productTitle} image`}
                width={80}
                height={80}
                data-testid={`${testIdPrefix}added-to-cart-alert-image`}
              />
            ) : (
              <ProductImageFallback className={styles.productImage} />
            )}
            <p className={styles.productTitle} data-testid={`${testIdPrefix}product-title`}>
              {productTitle}
            </p>
          </div>
          {bonusProducts?.length ? bonusProductsSection : null}
        </div>
        <div className={styles.actions}>
          <Button
            type="action"
            classes={styles.primaryActionButton}
            label="Checkout"
            onClick={onCheckoutClick!}
            data-testid={`${testIdPrefix}checkout-button`}
          />
          <Button
            type="linkWithIcon"
            classes={styles.secondaryActionButton}
            label="Review cart"
            icon={<Icon name="Cart" />}
            iconPosition="left"
            onClick={onReviewCartClick}
            data-testid={`${testIdPrefix}review-button`}
          />
        </div>
      </div>
    </div>
  );
};
